import { ChainID } from '@ankr.com/chains-list';
import { UserEndpointTokenMode } from 'multirpc-sdk';
import { createSelector } from 'reselect';

import { IFetchPrivateStatsParams } from 'modules/stats/actions/fetchPrivateStats';
import { RootState } from 'store';
import { getPrivateStatsProjectName } from 'modules/stats/utils/getPrivateStatsProjectName';
import { getUniqueArray } from 'modules/common/utils/getUniqueArray';
import { hasAllBlockchainsInWhitelist } from 'modules/projects/utils/hasAllBlockchainsInWhitelist';
import { selectAccountAddress } from 'modules/account/store/selectors';
import { selectPrivateAPIKeysCounts } from 'modules/stats/store/private/selectors';
import {
  selectBlockchainBySubchainId,
  selectChainIdsByPaths,
} from 'modules/chains/store/selectors';

import {
  IFetchProjectsWhitelistsBlockchainsParams,
  selectProjectsWhitelistsBlockchains,
} from '../actions/fetchProjectsWhitelistsBlockchains';
import { NewProjectStep } from '../types';
import { selectProjectWhitelist } from '../actions/fetchProjectWhitelist';

const selectNewProject = (state: RootState) => state.newProject;

export const selectNewProjectConfig = createSelector(
  selectNewProject,
  selectAccountAddress,
  (newProject, address = '') => newProject[address] || {},
);

export const selectDraftProject = createSelector(
  selectNewProjectConfig,
  ({ project = {} }) => project?.[NewProjectStep.General],
);

export const selectDraftUserEndpointToken = createSelector(
  selectNewProjectConfig,
  ({ project = {} }) => project?.[NewProjectStep.General]?.userEndpointToken,
);

export const selectProjectWhitelistByType = createSelector(
  selectProjectWhitelist,
  (_state: RootState, { type }: { type: UserEndpointTokenMode }) => type,
  (whitelist, type) =>
    whitelist?.lists?.filter(item => item.type === type) ?? [],
);

export const selectProjectRequestsCount = createSelector(
  selectPrivateAPIKeysCounts,
  (_state: RootState, _params: IFetchPrivateStatsParams, token: string) =>
    token,
  (counts, token) => {
    const projectName = getPrivateStatsProjectName(token);

    return Number(counts.find(({ name }) => name === projectName)?.count ?? 0);
  },
);

export const selectHasProjectsWithLastChain = createSelector(
  selectProjectsWhitelistsBlockchains,
  (
    state: RootState,
    _params: IFetchProjectsWhitelistsBlockchainsParams,
    chainId: ChainID,
  ) => ({ state, chainId }),
  (projects, { chainId: relatedChainId, state }) =>
    projects.some(project => {
      if (project.blockchains) {
        if (hasAllBlockchainsInWhitelist(project.blockchains)) {
          return false;
        }

        const chainIds = selectChainIdsByPaths(state, project.blockchains);

        const chains = chainIds
          .map(chainId => selectBlockchainBySubchainId(state, chainId))
          .filter(Boolean);

        const mainChainsIds = getUniqueArray(chains.map(chain => chain?.id));

        const isChainMainIdRelatedToCurrentSelector = mainChainsIds.some(
          chainId => chainId === relatedChainId,
        );

        return (
          mainChainsIds.length === 1 && isChainMainIdRelatedToCurrentSelector
        );
      }
    }),
);
